import { ProductCategoryLink } from '../../components/Navigation/types';
import { LobbiesData } from '../../components/Games/games.model';
import {
  GridWidth,
  SectionConfig,
  SectionType,
  TileRowCollection,
} from '../../components/Games/GameCard/GameCard.types';

type SectionKey = keyof Pick<
  TileRowCollection,
  'first_full_width_tile' | 'second_full_width_tile' | 'third_full_width_tile_block' | 'half_width_tile'
>;

const STANDARD_FALLBACK_SECTION = {
  type: SectionType.GRID,
} as const;

const DEFAULT_FALLBACK_SLIDE_SIZE = 12;

// Tile section game slice constants
const FIRST_TILE_START_INDEX = 0;
const FIRST_TILE_END_INDEX = 4;
const SECOND_TILE_START_INDEX = 4;
const SECOND_TILE_END_INDEX = 8;
const THIRD_TILE_START_INDEX = 8;
const THIRD_TILE_END_INDEX = 12;

// Layout required games count constants
export const HERO_LAYOUT_GAMES_COUNT = 1;
export const DOUBLE_LAYOUT_GAMES_COUNT = 2;
export const TALL_WIDE_LAYOUT_GAMES_COUNT = 3;
export const GRID_LAYOUT_GAMES_COUNT = 4;
export const COMPOSITE_LAYOUT_GAMES_COUNT = 5;

const getGamesCount = (sectionType: SectionType): number => {
  switch (sectionType) {
    case SectionType.GRID:
      return GRID_LAYOUT_GAMES_COUNT;
    case SectionType.TALL:
    case SectionType.TALL_REVERSE:
    case SectionType.WIDE:
    case SectionType.WIDE_REVERSE:
      return TALL_WIDE_LAYOUT_GAMES_COUNT;
    case SectionType.DOUBLE_TALL:
    case SectionType.DOUBLE_WIDE:
      return DOUBLE_LAYOUT_GAMES_COUNT;
    case SectionType.HERO:
      return HERO_LAYOUT_GAMES_COUNT;
    // Composite section types
    case SectionType.TALL_PLUS_GRID:
    case SectionType.GRID_PLUS_TALL:
      return COMPOSITE_LAYOUT_GAMES_COUNT; // 1 (TALL) + 4 (GRID)
    case SectionType.HERO_PLUS_TALL:
    case SectionType.TALL_PLUS_HERO:
      return DOUBLE_LAYOUT_GAMES_COUNT; // 1 (HERO) + 1 (TALL)
    default:
      return GRID_LAYOUT_GAMES_COUNT;
  }
};

const buildFallbackSlides = (allGameIds: string[], index: number, title: string | undefined): TileRowCollection[] => {
  const totalGames = allGameIds.length;
  const numberOfSlides = Math.max(1, Math.ceil(totalGames / DEFAULT_FALLBACK_SLIDE_SIZE));

  return Array.from({ length: numberOfSlides }, (_, slideIndex) => {
    const startIndex = slideIndex * DEFAULT_FALLBACK_SLIDE_SIZE;
    const gameIds = allGameIds.slice(startIndex, startIndex + DEFAULT_FALLBACK_SLIDE_SIZE);

    return {
      id: `fallback-${index}-slide-${slideIndex}`,
      SeeMoreViewAll: true,
      Width: GridWidth.FULL,
      first_full_width_tile: {
        ...STANDARD_FALLBACK_SECTION,
        games: gameIds.slice(FIRST_TILE_START_INDEX, FIRST_TILE_END_INDEX),
      },
      second_full_width_tile: {
        ...STANDARD_FALLBACK_SECTION,
        games: gameIds.slice(SECOND_TILE_START_INDEX, SECOND_TILE_END_INDEX),
      },
      third_full_width_tile_block: {
        ...STANDARD_FALLBACK_SECTION,
        games: gameIds.slice(THIRD_TILE_START_INDEX, THIRD_TILE_END_INDEX),
      },
      internal_name: title || `Fallback ${index + 1}`,
    };
  });
};

type SectionBlueprint = Array<{
  key: SectionKey;
  type: SectionType;
  gamesCount: number;
  customTileId?: string;
}>;

const createSectionBlueprint = (apiTileConfig: ProductCategoryLink['tile_row_collection']): SectionBlueprint =>
  (
    [
      'first_full_width_tile',
      'second_full_width_tile',
      'third_full_width_tile_block',
      'half_width_tile',
    ] as SectionKey[]
  )
    .map((sectionKey) => {
      const section = apiTileConfig?.[sectionKey];

      if (!section) return null;

      const sectionType = section.type as SectionType;

      return {
        key: sectionKey,
        type: sectionType,
        gamesCount: getGamesCount(sectionType),
        customTileId: section.custom_tile_id,
      };
    })
    .filter(Boolean) as SectionBlueprint;

const consumeBlueprintForSlides = (
  sectionBlueprint: SectionBlueprint,
  gameIds: string[],
  baseInternalName: string,
  apiTileConfig: NonNullable<ProductCategoryLink['tile_row_collection']>
): {
  slides: TileRowCollection[];
  consumedGames: number;
} => {
  const slides: TileRowCollection[] = [];

  let pointer = 0;
  let slideIndex = 0;

  while (pointer < gameIds.length && sectionBlueprint.length) {
    const slideStartPointer = pointer;
    const slideSections: Partial<Record<SectionKey, SectionConfig>> = {};
    let slideComplete = true;

    for (const section of sectionBlueprint) {
      const nextPointer = pointer + section.gamesCount;
      const sectionGames = gameIds.slice(pointer, nextPointer);

      if (sectionGames.length < section.gamesCount) {
        slideComplete = false;
        pointer = slideStartPointer;
        break;
      }

      slideSections[section.key] = {
        type: section.type,
        games: sectionGames,
        ...(section.customTileId ? { custom_tile_id: section.customTileId } : {}),
      };

      pointer = nextPointer;
    }

    if (!slideComplete || Object.keys(slideSections).length === 0) {
      break;
    }

    const slideId = slideIndex === 0 ? apiTileConfig.id : `${apiTileConfig.id}-slide-${slideIndex + 1}`;
    const slideInternalName = slideIndex === 0 ? baseInternalName : `${baseInternalName} - ${slideIndex + 1}`;

    slides.push({
      id: slideId,
      SeeMoreViewAll: apiTileConfig.SeeMoreViewAll,
      Width: apiTileConfig.Width === 'FULL' ? GridWidth.FULL : GridWidth.HALF,
      ...slideSections,
      internal_name: slideInternalName,
    });

    slideIndex += 1;
  }

  return {
    slides,
    consumedGames: pointer,
  };
};

const buildFallbackForRemainder = (
  remainingGameIds: string[],
  slideIndexOffset: number,
  apiTileConfig: NonNullable<ProductCategoryLink['tile_row_collection']>,
  baseInternalName: string
): TileRowCollection[] => {
  if (!remainingGameIds.length) {
    return [];
  }

  const fallbackSlides = Math.ceil(remainingGameIds.length / DEFAULT_FALLBACK_SLIDE_SIZE);

  return Array.from({ length: fallbackSlides }, (_, slide) => {
    const start = slide * DEFAULT_FALLBACK_SLIDE_SIZE;
    const chunk = remainingGameIds.slice(start, start + DEFAULT_FALLBACK_SLIDE_SIZE);

    return {
      id: `${apiTileConfig.id}-fallback-${slide + 1 + slideIndexOffset}`,
      SeeMoreViewAll: apiTileConfig.SeeMoreViewAll,
      Width: apiTileConfig.Width === 'FULL' ? GridWidth.FULL : GridWidth.HALF,
      first_full_width_tile: {
        ...STANDARD_FALLBACK_SECTION,
        games: chunk.slice(FIRST_TILE_START_INDEX, FIRST_TILE_END_INDEX),
      },
      second_full_width_tile: {
        ...STANDARD_FALLBACK_SECTION,
        games: chunk.slice(SECOND_TILE_START_INDEX, SECOND_TILE_END_INDEX),
      },
      third_full_width_tile_block: {
        ...STANDARD_FALLBACK_SECTION,
        games: chunk.slice(THIRD_TILE_START_INDEX, THIRD_TILE_END_INDEX),
      },
      internal_name: `${baseInternalName} - Fallback ${slide + 1}`,
    };
  });
};

export const createTileRowCollections = (
  lobbyItems: LobbiesData['lobbyItems'],
  categories: ProductCategoryLink[]
): TileRowCollection[] =>
  lobbyItems.flatMap((item, index) => {
    const allGameIds = item.games?.map((game) => game.id || game.gameId) || [];
    const categoryConfig = categories.find((cat) => cat.label === item.title)?.tile_row_collection;
    const apiTileConfig = categoryConfig || item.tile_row_collection;

    if (!apiTileConfig) {
      return buildFallbackSlides(allGameIds, index, item.title);
    }

    const baseInternalName = apiTileConfig.internal_name || item.title || `Category ${index + 1}`;
    const sectionBlueprint = createSectionBlueprint(apiTileConfig);

    const { slides, consumedGames } = consumeBlueprintForSlides(
      sectionBlueprint,
      allGameIds,
      baseInternalName,
      apiTileConfig
    );

    const remainingGames = allGameIds.slice(consumedGames);
    const fallbackSlides = buildFallbackForRemainder(remainingGames, slides.length, apiTileConfig, baseInternalName);

    return [...slides, ...fallbackSlides];
  });

export const getRequiredGameCount = (sectionType: SectionType): number => {
  switch (sectionType) {
    case SectionType.GRID:
      return GRID_LAYOUT_GAMES_COUNT;
    case SectionType.TALL:
    case SectionType.TALL_REVERSE:
    case SectionType.WIDE:
    case SectionType.WIDE_REVERSE:
      return TALL_WIDE_LAYOUT_GAMES_COUNT;
    case SectionType.DOUBLE_TALL:
    case SectionType.DOUBLE_WIDE:
      return DOUBLE_LAYOUT_GAMES_COUNT;
    case SectionType.HERO:
      return HERO_LAYOUT_GAMES_COUNT;
    case SectionType.TALL_PLUS_GRID:
    case SectionType.GRID_PLUS_TALL:
      return COMPOSITE_LAYOUT_GAMES_COUNT;
    case SectionType.HERO_PLUS_TALL:
    case SectionType.TALL_PLUS_HERO:
      return HERO_LAYOUT_GAMES_COUNT;
    default:
      return HERO_LAYOUT_GAMES_COUNT;
  }
};
